import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, ArrowLeft, Loader2, Globe, Rocket } from 'lucide-react'
import { cn } from '@/utils/cn'
import type { ProjectCategory } from '@/types'

export type OnboardingPath = 'build_new' | 'existing'

export interface OnboardingProjectData {
  path: OnboardingPath
  businessName: string
  projectCategory: ProjectCategory
  projectDescription: string
  industry: string
  targetAudience: string
  liveUrl: string
  platform: 'shopify' | 'wordpress' | 'other'
}

interface OnboardingStepProjectProps {
  onContinue: (project: OnboardingProjectData) => void
  onBack: () => void
  onSkip: () => void
  isCreating: boolean
}

type SubStep = 'path_selection' | 'business_context'

const CATEGORIES: { id: ProjectCategory; label: string }[] = [
  { id: 'website', label: 'Website' },
  { id: 'saas', label: 'Web App' },
  { id: 'ecommerce', label: 'E-commerce' },
  { id: 'mobile_app', label: 'Mobile App' },
]

const INDUSTRY_SUGGESTIONS = [
  'Technology', 'E-commerce', 'Healthcare', 'Real Estate',
  'Restaurant', 'Agency', 'Finance', 'Education',
]

function detectPlatform(url: string): 'shopify' | 'wordpress' | 'other' {
  const lower = url.toLowerCase()
  if (lower.includes('.myshopify.com') || lower.includes('shopify')) return 'shopify'
  if (lower.includes('wordpress.com') || lower.includes('.wp.') || lower.includes('/wp-admin') || lower.includes('/wp-content')) return 'wordpress'
  return 'other'
}

export function OnboardingStepProject({ onContinue, onBack, onSkip, isCreating }: OnboardingStepProjectProps) {
  const [subStep, setSubStep] = useState<SubStep>('path_selection')
  const [path, setPath] = useState<OnboardingPath | null>(null)

  // Form fields (persisted across sub-step navigation)
  const [businessName, setBusinessName] = useState('')
  const [projectCategory, setProjectCategory] = useState<ProjectCategory>('website')
  const [projectDescription, setProjectDescription] = useState('')
  const [industry, setIndustry] = useState('')
  const [targetAudience, setTargetAudience] = useState('')
  const [liveUrl, setLiveUrl] = useState('')
  const [platform, setPlatform] = useState<'shopify' | 'wordpress' | 'other'>('other')

  const handleSelectPath = (selected: OnboardingPath) => {
    setPath(selected)
    setSubStep('business_context')
  }

  const handleBackToPathSelection = () => {
    setSubStep('path_selection')
  }

  const handleUrlBlur = () => {
    const detected = detectPlatform(liveUrl)
    setPlatform(detected)
    if (detected === 'shopify') setProjectCategory('ecommerce')
  }

  const isFormValid = () => {
    if (!businessName.trim()) return false
    if (path === 'build_new' && !projectDescription.trim()) return false
    if (path === 'existing' && !liveUrl.trim()) return false
    return true
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!path || !isFormValid()) return
    onContinue({
      path,
      businessName: businessName.trim(),
      projectCategory,
      projectDescription: projectDescription.trim(),
      industry: industry.trim(),
      targetAudience: targetAudience.trim(),
      liveUrl: liveUrl.trim(),
      platform,
    })
  }

  return (
    <motion.div
      key="step-project"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="max-w-xl mx-auto"
    >
      <AnimatePresence mode="wait">
        {/* ── Sub-step A: Path Selection ── */}
        {subStep === 'path_selection' && (
          <motion.div
            key="path-selection"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-framed-neutral-800 tracking-tight font-display mb-2">
                Add your first project
              </h2>
              <p className="text-sm text-framed-neutral-500">
                Choose how you'd like to get started.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {/* Build new */}
              <button
                onClick={() => handleSelectPath('build_new')}
                className="group text-left bg-white rounded-xl border border-framed-neutral-300 p-6 hover:border-framed-neutral-400 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-framed-neutral-200 flex items-center justify-center mb-4 group-hover:bg-framed-neutral-300 transition-colors">
                  <Rocket className="w-5 h-5 text-framed-neutral-600" />
                </div>
                <h3 className="text-base font-semibold text-framed-neutral-800 mb-1">
                  Build something new
                </h3>
                <p className="text-sm text-framed-neutral-500 mb-5">
                  Start from scratch. We'll guide you through discovery, design, and build.
                </p>
                <div className="flex items-center gap-2 text-sm font-medium text-framed-neutral-800 group-hover:text-framed-neutral-700">
                  Start building
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </button>

              {/* Connect existing */}
              <button
                onClick={() => handleSelectPath('existing')}
                className="group text-left bg-white rounded-xl border border-framed-neutral-300 p-6 hover:border-framed-neutral-400 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-framed-neutral-200 flex items-center justify-center mb-4 group-hover:bg-framed-neutral-300 transition-colors">
                  <Globe className="w-5 h-5 text-framed-neutral-600" />
                </div>
                <h3 className="text-base font-semibold text-framed-neutral-800 mb-1">
                  Connect an existing project
                </h3>
                <p className="text-sm text-framed-neutral-500 mb-5">
                  Already have a website or app? Connect it for feedback, monitoring, and maintenance.
                </p>
                <div className="flex items-center gap-2 text-sm font-medium text-framed-neutral-800 group-hover:text-framed-neutral-700">
                  Set up
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </div>
              </button>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={onSkip}
                className="text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors"
              >
                Skip, create later
              </button>
            </div>
          </motion.div>
        )}

        {/* ── Sub-step B: Business Context Form ── */}
        {subStep === 'business_context' && path && (
          <motion.div
            key="business-context"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-framed-neutral-800 tracking-tight font-display mb-2">
                {path === 'build_new' ? 'Tell us about your project' : 'Connect your project'}
              </h2>
              <p className="text-sm text-framed-neutral-500">
                {path === 'build_new'
                  ? 'A little context helps us set up the right workflow for you.'
                  : 'We just need a few details to get your project set up.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-framed-neutral-300 p-6 space-y-5">
              {/* Business name */}
              <div>
                <label className="text-sm font-medium text-framed-neutral-700 block mb-1.5">
                  Business name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="Acme Inc."
                  required
                  className="w-full h-11 px-4 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                />
              </div>

              {/* Existing path: Live URL right after business name */}
              {path === 'existing' && (
                <div>
                  <label className="text-sm font-medium text-framed-neutral-700 block mb-1.5">
                    Live URL <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    value={liveUrl}
                    onChange={(e) => setLiveUrl(e.target.value)}
                    onBlur={handleUrlBlur}
                    placeholder="https://example.com"
                    required
                    className="w-full h-11 px-4 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                  />
                  {liveUrl && platform !== 'other' && (
                    <div className="mt-1.5">
                      <span className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                        platform === 'shopify'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      )}>
                        {platform === 'shopify' ? 'Shopify' : 'WordPress'} detected
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Build new: Project category */}
              {path === 'build_new' && (
                <div>
                  <label className="text-sm font-medium text-framed-neutral-700 block mb-2">
                    What are you building? <span className="text-red-400">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CATEGORIES.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setProjectCategory(cat.id)}
                        className={cn(
                          'px-3.5 py-1.5 rounded-lg text-sm border-2 transition-all',
                          projectCategory === cat.id
                            ? 'border-framed-yellow-400 bg-framed-yellow-100/50 text-framed-neutral-800 font-medium'
                            : 'border-framed-neutral-300 text-framed-neutral-500 hover:border-framed-neutral-400'
                        )}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Build new: Project description */}
              {path === 'build_new' && (
                <div>
                  <label className="text-sm font-medium text-framed-neutral-700 block mb-1.5">
                    Tell us about your project <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    placeholder="e.g. A portfolio site for my photography business with a booking form and gallery..."
                    required
                    rows={3}
                    className="w-full px-4 py-3 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all resize-none"
                  />
                </div>
              )}

              {/* Industry (both paths) */}
              <div>
                <label className="text-sm font-medium text-framed-neutral-700 block mb-1.5">
                  Industry
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g. Technology"
                  className="w-full h-11 px-4 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {INDUSTRY_SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => setIndustry(suggestion)}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-xs border transition-colors cursor-pointer',
                        industry === suggestion
                          ? 'border-framed-yellow-400 bg-framed-yellow-100/50 text-framed-neutral-700'
                          : 'border-framed-neutral-300 text-framed-neutral-500 hover:border-framed-neutral-400'
                      )}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>

              {/* Build new: Target audience */}
              {path === 'build_new' && (
                <div>
                  <label className="text-sm font-medium text-framed-neutral-700 block mb-1.5">
                    Target audience
                  </label>
                  <input
                    type="text"
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    placeholder="e.g. Small business owners aged 25-45"
                    className="w-full h-11 px-4 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                  />
                </div>
              )}

              {/* Build new: Reference URL */}
              {path === 'build_new' && (
                <div>
                  <label className="text-sm font-medium text-framed-neutral-700 block mb-1.5">
                    Reference URL
                  </label>
                  <input
                    type="url"
                    value={liveUrl}
                    onChange={(e) => setLiveUrl(e.target.value)}
                    onBlur={handleUrlBlur}
                    placeholder="https://example.com"
                    className="w-full h-11 px-4 text-sm border-2 border-framed-neutral-300 rounded-lg bg-white focus:border-framed-yellow focus:ring-2 focus:ring-framed-yellow-500/30 outline-none transition-all"
                  />
                  {liveUrl && platform !== 'other' && (
                    <div className="mt-1.5">
                      <span className={cn(
                        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                        platform === 'shopify'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      )}>
                        {platform === 'shopify' ? 'Shopify' : 'WordPress'} detected
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={handleBackToPathSelection}
                  className="inline-flex items-center gap-1.5 text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid() || isCreating}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-framed-neutral-800 text-white rounded-lg font-medium hover:bg-framed-neutral-900 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      Add project
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>

            <div className="text-center mt-4">
              <button
                onClick={onSkip}
                className="text-sm text-framed-neutral-500 hover:text-framed-neutral-700 transition-colors"
              >
                Skip, create later
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
